/** Einfacher Router über die History-API; jede Ansicht räumt beim Verlassen auf (Kamera, Timer). */

export type Aufraeumen = () => void;
export type Ansicht = (ziel: HTMLElement, params: Record<string, string>, suche: URLSearchParams) => void | Aufraeumen | Promise<void | Aufraeumen>;

/** Behandelt eine Zurück-Navigation vor dem Router; true heißt erledigt, die Ansicht bleibt. */
export type ZurueckHalter = (ereignis: PopStateEvent) => boolean;

interface Route {
  muster: RegExp;
  namen: string[];
  ansicht: Ansicht;
  titel: string;
}

const routen: Route[] = [];
let aufraeumen: Aufraeumen | undefined;
let ziel: HTMLElement;
let wache: ((pfad: string) => Promise<string | null>) | undefined;
let zurueckHalter: ZurueckHalter | undefined;
/** Zählt Navigationen; eine überholte Ansicht verwirft ihr Ergebnis. */
let navigation = 0;

/** Registriert eine Route, z. B. „/pruefung/:ansicht“. */
export function route(pfad: string, titel: string, ansicht: Ansicht): void {
  const namen: string[] = [];
  const muster = new RegExp(
    `^${pfad.replace(/:([a-z]+)/g, (_, name: string) => {
      namen.push(name);
      return '([^/]+)';
    })}/?$`,
  );
  routen.push({ muster, namen, ansicht, titel });
}

/** Leitet vor dem Anzeigen um, z. B. zur Einrichtung, solange das Gerät nicht angemeldet ist. */
export function setzeWache(funktion: (pfad: string) => Promise<string | null>): void {
  wache = funktion;
}

/**
 * Lässt die aktuelle Ansicht die nächste Zurück-Navigation zuerst behandeln, z. B.
 * einen offenen Dialog schließen. Gibt die Funktion zum Abmelden zurück.
 */
export function halteZurueck(halter: ZurueckHalter): Aufraeumen {
  zurueckHalter = halter;
  return () => {
    if (zurueckHalter === halter) zurueckHalter = undefined;
  };
}

export function navigiere(pfad: string, ersetzen = false): void {
  if (ersetzen) history.replaceState(null, '', pfad);
  else history.pushState(null, '', pfad);
  void zeige();
}

export async function zeige(): Promise<void> {
  const nummer = ++navigation;
  const umleitung = await wache?.(location.pathname);
  if (nummer !== navigation) return;
  if (umleitung && umleitung !== location.pathname + location.search) {
    history.replaceState(null, '', umleitung);
  }
  aufraeumen?.();
  aufraeumen = undefined;
  const pfad = location.pathname;
  const treffer = routen.map((r) => ({ r, t: r.muster.exec(pfad) })).find((x) => x.t);
  ziel.replaceChildren();
  if (!treffer?.t) {
    document.title = 'Nicht gefunden – TFM Gerätetest';
    const h1 = document.createElement('h1');
    h1.textContent = 'Seite nicht gefunden';
    ziel.append(h1);
    return;
  }
  const params: Record<string, string> = {};
  treffer.r.namen.forEach((name, i) => {
    params[name] = decodeURIComponent(treffer.t?.[i + 1] ?? '');
  });
  document.title = `${treffer.r.titel} – TFM Gerätetest`;
  // Eigener Container je Ansicht: Eine überholte Ansicht schreibt so nie in die neue.
  const behaelter = document.createElement('div');
  ziel.append(behaelter);
  const ergebnis = await treffer.r.ansicht(behaelter, params, new URLSearchParams(location.search));
  if (nummer !== navigation) {
    if (typeof ergebnis === 'function') ergebnis();
    behaelter.remove();
    return;
  }
  if (typeof ergebnis === 'function') aufraeumen = ergebnis;
  // Fokus auf die Überschrift, damit Screenreader den Seitenwechsel ansagen.
  const ueberschrift = ziel.querySelector('h1');
  if (ueberschrift) {
    ueberschrift.setAttribute('tabindex', '-1');
    ueberschrift.focus({ preventScroll: true });
  }
  if (!location.hash) window.scrollTo(0, 0);
  else document.getElementById(location.hash.slice(1))?.scrollIntoView();
}

export function starteRouter(inhalt: HTMLElement): void {
  ziel = inhalt;
  window.addEventListener('popstate', (ereignis) => {
    if (zurueckHalter?.(ereignis)) return;
    void zeige();
  });
  document.addEventListener('click', (ereignis) => {
    if (ereignis.defaultPrevented || ereignis.button !== 0 || ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey || ereignis.altKey) return;
    const link = (ereignis.target as Element | null)?.closest('a');
    if (!link || link.target || link.hasAttribute('download') || link.origin !== location.origin) return;
    ereignis.preventDefault();
    navigiere(link.pathname + link.search + link.hash);
  });
  void zeige();
}
